/**
 * Meta Pixel SPA helpers.
 * Bootstrap (fbq init) is in public/index.html — init only, no PageView there.
 */

import {
  buildMarketingPageViewKey,
  shouldTrackMarketingPageView,
} from './shared/marketingRouteExclusions';

const PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;
const ENABLED = process.env.REACT_APP_META_PIXEL_ENABLED === 'true';

const DEFAULT_ALLOWED_HOSTS = ['yovotrip.com', 'www.yovotrip.com'];

const ALLOWED_HOSTS = (
  process.env.REACT_APP_META_PIXEL_ALLOWED_HOSTS || DEFAULT_ALLOWED_HOSTS.join(',')
)
  .split(',')
  .map((host) => host.trim())
  .filter(Boolean);

/** @type {string | null} */
let lastTrackedPageKey = null;

/**
 * Dedupe key for SPA PageView tracking.
 * React Router provides a stable location.search string per navigation.
 *
 * @param {string | undefined | null} pathname
 * @param {string | undefined | null} search
 * @returns {string}
 */
export function buildMetaPageViewKey(pathname, search) {
  return buildMarketingPageViewKey(pathname, search);
}

/**
 * @returns {boolean}
 */
export function isAllowedMetaPixelHostname() {
  if (typeof window === 'undefined') {
    return false;
  }
  return ALLOWED_HOSTS.includes(window.location.hostname);
}

/**
 * @returns {boolean}
 */
export function isMetaPixelEnabled() {
  return (
    ENABLED &&
    Boolean(PIXEL_ID) &&
    isAllowedMetaPixelHostname() &&
    typeof window !== 'undefined' &&
    typeof window.fbq === 'function'
  );
}

/**
 * @param {string | undefined | null} pathname
 * @returns {boolean}
 */
export function shouldTrackMetaPageView(pathname) {
  return shouldTrackMarketingPageView(pathname);
}

/**
 * Track one PageView per distinct pathname + search.
 * Dedupes React StrictMode double-effects in development.
 *
 * @param {string | undefined | null} pathname
 * @param {string | undefined | null} search
 */
export function trackMetaPageView(pathname, search) {
  if (!isMetaPixelEnabled()) {
    return;
  }

  if (!shouldTrackMetaPageView(pathname)) {
    return;
  }

  const pageKey = buildMetaPageViewKey(pathname, search);

  if (lastTrackedPageKey === pageKey) {
    return;
  }

  lastTrackedPageKey = pageKey;
  window.fbq('track', 'PageView');
}
